// A player's public profile (wave 12, account track): /u/<handle>. Name, the games they love, the creators they
// follow, as their privacy settings allow (accounts decides: GET /v1/users/by-handle/:handle and /users/:id/shelf).

import { api, ApiError } from "../api.ts";
import { gameById, getCatalogue, type Game } from "../catalogue.ts";
import { gameCard } from "../components.ts";
import { clear, errorBox, h, link } from "../dom.ts";
import { setTitle } from "../router.ts";
import { coverUrl } from "../rows.ts";
import { currentMe } from "../session.ts";
import { accountStyles } from "./me-kit.ts";
import { clipCard, type Clip } from "../clips.ts";
import { profileExtras } from "./profile-people.ts";

interface PublicUser {
  id: string;
  handle: string | null;
  displayName: string | null;
  /** the avatar portrait, when their profile is open to you (accounts users.ts toViewerUser) */
  avatarPortrait?: string | null;
}
interface Shelf {
  visible: boolean;
  hearts: string[];
  creators: string[];
  games: string[];
}

export async function profilePage(params: Record<string, string>): Promise<HTMLElement> {
  accountStyles();
  const handle = (params.handle ?? "").replace(/^@/, "");
  setTitle(`@${handle}`);
  const root = h("div", { class: "acct acct-page profile-page" }, h("p", { class: "dim" }, "Loading…"));
  let u: PublicUser;
  try {
    u = await api<PublicUser>("accounts", `/users/by-handle/${encodeURIComponent(handle)}`);
  } catch (e) {
    clear(
      root,
      e instanceof ApiError && e.status === 404
        ? h("div", { class: "acct-mod acct-empty" }, h("h1", null, "Nobody here yet"), h("p", { class: "dim" }, `There's no player called @${handle}.`), link("/browse", { class: "btn" }, "Browse games"))
        : errorBox(e),
    );
    return root;
  }
  const [shelf, cat] = await Promise.all([api<Shelf>("accounts", `/users/${u.id}/shelf`).catch(() => null), getCatalogue().catch(() => null)]);
  // their clips are public on game pages, but gathered under a name they follow the profile's privacy: shown only
  // when the shelf is (profile and library open to this viewer); an older community without the read hides them
  const clips = shelf?.visible
    ? await api<{ clips: Clip[] }>("community", `/users/${encodeURIComponent(u.id)}/clips?limit=6`).then((r) => r.clips.filter((c) => c.status === "visible"), () => [])
    : [];
  const self = currentMe()?.id === u.id;
  // wave 13 (people): the card they wrote, their posts, Follow and Block (social)
  const people = await profileExtras(u.id, self);
  const name = u.displayName ?? `@${u.handle ?? handle}`;
  setTitle(name);
  const loved = cat && shelf ? shelf.hearts.map((id) => gameById(cat, id)).filter((g): g is Game => !!g) : [];
  const creators = cat && shelf ? shelf.creators.map((id) => cat.creators.find((c) => c.id === id)).filter((c): c is NonNullable<typeof c> => !!c) : [];
  const followed = cat && shelf ? shelf.games.map((id) => gameById(cat, id)).filter((g): g is Game => !!g) : [];

  const banner = h("div", { class: "acct-hello-bg", "aria-hidden": "true" });
  const lead = loved[0] ?? followed[0];
  if (lead && coverUrl(lead, "full")) banner.style.backgroundImage = `url("${coverUrl(lead, "full")}")`;
  if (people.card?.banner?.startsWith("preset:")) banner.dataset.preset = people.card.banner.slice(7);
  void people.banner.then((art) => art && (banner.style.backgroundImage = `url("${art}")`));
  const initial = name.replace(/^@/, "").slice(0, 1).toUpperCase();

  const blocks: (HTMLElement | null)[] = [people.about, people.posts];
  if (clips.length)
    blocks.push(
      h(
        "section",
        { class: "acct-mod" },
        h("div", { class: "acct-mod-head" }, h("h2", null, "Their clips")),
        h("ul", { class: "clip-grid" }, clips.map((c) => clipCard(c, { id: c.game_id, title: (cat && gameById(cat, c.game_id)?.title) ?? null, play: "" }))),
      ),
    );
  if (loved.length) blocks.push(h("section", { class: "acct-mod" }, h("div", { class: "acct-mod-head" }, h("h2", null, "Games they love")), h("ul", { class: "acct-strip" }, loved.map((g) => gameCard(g)))));
  if (followed.length) blocks.push(h("section", { class: "acct-mod" }, h("div", { class: "acct-mod-head" }, h("h2", null, "Games they follow")), h("ul", { class: "acct-strip" }, followed.map((g) => gameCard(g)))));
  if (creators.length) blocks.push(h("section", { class: "acct-mod" }, h("div", { class: "acct-mod-head" }, h("h2", null, "Creators they follow")), h("div", { class: "acct-chips" }, creators.map((c) => link(`/c/${c.id}`, { class: "acct-chip" }, c.name)))));
  if (blocks.length === 2 && !people.about)
    blocks.push(
      h(
        "section",
        { class: "acct-mod acct-empty" },
        h("p", { class: "dim" }, shelf && !shelf.visible ? (self ? "Only you can see your games right now. Change who sees them in Settings → Privacy." : "This player keeps their games private.") : self ? "Heart games and follow creators and they'll show here." : "Nothing to show yet."),
        self ? link("/me/settings#privacy", { class: "btn ghost" }, "Privacy settings") : null,
      ),
    );

  clear(
    root,
    h(
      "div",
      { class: "acct-hello profile" },
      banner,
      h(
        "div",
        { class: "acct-hello-in" },
        u.avatarPortrait
          ? h("div", { class: "acct-face has-picture", "aria-hidden": "true", style: `background-image:url("${encodeURI(u.avatarPortrait)}")` })
          : h("div", { class: "acct-face", "aria-hidden": "true" }, initial),
        h("h1", null, name),
        u.handle && u.displayName ? h("p", { class: "lede" }, `@${u.handle}`) : null,
        people.actions,
      ),
    ),
    h("div", { class: "acct-mods" }, blocks),
  );
  return root;
}
